// Returns the display label of the next upcoming, visible, active
// registration period (based on start_date). Falls back to the default
// period label if no future period is found, or null when the table is
// empty. Used by public "Coming Soon" cards to advertise the next window
// without hardcoding semester names.
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { formatPeriodLabel, type PeriodShape } from "@/lib/registrationPeriodLabel";

interface PeriodRow extends PeriodShape {
  id: string;
  is_default: boolean | null;
  is_visible: boolean | null;
  is_active: boolean | null;
}

let cachedLabel: string | null | undefined = undefined;
const listeners = new Set<(v: string | null) => void>();

async function loadOnce() {
  const { data } = await (supabase as any)
    .from("registration_periods")
    .select("id, display_label, name, period_type, month_number, year, academic_year, start_date, is_default, is_visible, is_active")
    .eq("is_visible", true)
    .eq("is_active", true);
  const rows: PeriodRow[] = (data as PeriodRow[]) ?? [];
  const todayIso = new Date().toISOString().slice(0, 10);
  const upcoming = rows
    .filter((r) => r.start_date && r.start_date >= todayIso)
    .sort((a, b) => (a.start_date! < b.start_date! ? -1 : 1));
  const pick =
    upcoming[0] ??
    rows.find((r) => r.is_default) ??
    rows[0] ??
    null;
  const label = pick ? formatPeriodLabel(pick) : null;
  cachedLabel = label;
  for (const l of listeners) l(label);
}

export function useNextRegistrationPeriodLabel(): string | null {
  const [label, setLabel] = useState<string | null>(cachedLabel ?? null);
  useEffect(() => {
    listeners.add(setLabel);
    if (cachedLabel === undefined) loadOnce();
    return () => { listeners.delete(setLabel); };
  }, []);
  return label;
}
